import { useRef, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { config } from '../data/config';

const { FiBold, FiItalic, FiList, FiLink, FiMinus } = FiIcons;

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  maxLength?: number;
  placeholder?: string;
}

const ALLOWED_TAGS = [
  'h2', 'h3', 'p', 'br', 'b', 'i', 'strong', 'em',
  'ul', 'ol', 'li', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
];
const ALLOWED_ATTR = ['href', 'target'];

// ─── Internal helper functions ───

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function processInlineMarkdown(text: string): string {
  let result = text;
  // **bold** → <strong>bold</strong>
  result = result.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
  // __bold__ → <strong>bold</strong>
  result = result.replace(/__([^_]+?)__/g, '<strong>$1</strong>');
  // *italic* → <em>italic</em> (conservative word-boundary matching)
  result = result.replace(
    /(^|[\s(])\*([^*\s][^*]*?[^*\s]?)\*(?=[\s),.;:!?]|$)/gm,
    '$1<em>$2</em>'
  );
  // _italic_ → <em>italic</em> (conservative word-boundary matching)
  result = result.replace(
    /(^|[\s(])_([^_\s][^_]*?[^_\s]?)_(?=[\s),.;:!?]|$)/gm,
    '$1<em>$2</em>'
  );
  return result;
}

function normaliseRichHtml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function processNode(node: HTMLElement): void {
    // Process children bottom-up so inner transformations happen first
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        processNode(child as HTMLElement);
      }
    }

    const tag = node.tagName.toLowerCase();

    // Remove dangerous elements entirely
    if ([
      'script', 'style', 'iframe', 'object', 'embed',
      'applet', 'form', 'meta', 'link', 'noscript', 'template',
    ].includes(tag)) {
      node.remove();
      return;
    }

    // h1 → h2
    if (tag === 'h1') {
      const repl = doc.createElement('h2');
      while (node.firstChild) repl.appendChild(node.firstChild);
      node.replaceWith(repl);
      return;
    }

    // h4, h5, h6 → h3
    if (['h4', 'h5', 'h6'].includes(tag)) {
      const repl = doc.createElement('h3');
      while (node.firstChild) repl.appendChild(node.firstChild);
      node.replaceWith(repl);
      return;
    }

    // Convert visually-styled spans to semantic elements
    if (tag === 'span') {
      const fw = node.style.fontWeight || '';
      const fs = node.style.fontStyle || '';
      const isBold = fw === 'bold' || fw === 'bolder' ||
        (fw !== '' && !isNaN(Number(fw)) && Number(fw) >= 700);
      const isItalic = fs === 'italic' || fs === 'oblique';

      if (isBold) {
        const repl = doc.createElement('strong');
        while (node.firstChild) repl.appendChild(node.firstChild);
        node.replaceWith(repl);
        return;
      }
      if (isItalic) {
        const repl = doc.createElement('em');
        while (node.firstChild) repl.appendChild(node.firstChild);
        node.replaceWith(repl);
        return;
      }

      // Unwrap plain spans preserving their content
      const frag = doc.createDocumentFragment();
      while (node.firstChild) frag.appendChild(node.firstChild);
      node.replaceWith(frag);
      return;
    }

    // Convert or unwrap divs to preserve paragraph boundaries
    if (tag === 'div') {
      const hasBlockChildren = Array.from(node.childNodes).some(
        c => c.nodeType === Node.ELEMENT_NODE &&
          [
            'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'table', 'blockquote', 'div',
          ].includes((c as HTMLElement).tagName.toLowerCase())
      );
      if (hasBlockChildren) {
        // Unwrap: children are already block elements
        const frag = doc.createDocumentFragment();
        while (node.firstChild) frag.appendChild(node.firstChild);
        node.replaceWith(frag);
      } else {
        // Convert to paragraph to preserve line boundary
        const p = doc.createElement('p');
        while (node.firstChild) p.appendChild(node.firstChild);
        node.replaceWith(p);
      }
      return;
    }
  }

  processNode(doc.body);
  return doc.body.innerHTML;
}

function convertPlainTextToStructuredHtml(text: string): string {
  // Normalise line endings
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');

  const result: string[] = [];
  let inBulletList = false;
  let inNumberedList = false;
  let isFirstContent = true;

  function closeOpenList() {
    if (inBulletList) { result.push('</ul>'); inBulletList = false; }
    if (inNumberedList) { result.push('</ol>'); inNumberedList = false; }
  }

  function getNextNonBlank(from: number): string | null {
    for (let j = from; j < lines.length; j++) {
      const t = lines[j].trim();
      if (t !== '') return t;
    }
    return null;
  }

  // A label/value line contains a colon that is not a URL protocol colon
  function isLabelValue(line: string): boolean {
    const ci = line.indexOf(':');
    if (ci <= 0) return false;
    const before = line.substring(0, ci).trim();
    // Reject URL protocols
    if (/^(https?|ftp|ftps|mailto|tel|data|javascript)$/i.test(before)) return false;
    // Reject colon followed by // (URL path like https://...)
    if (line.substring(ci + 1, ci + 3) === '//') return false;
    // Label should be reasonably short
    if (ci > 80) return false;
    return true;
  }

  // A short line that looks like a section heading
  function looksLikeHeading(line: string): boolean {
    if (line.length === 0 || line.length > 60) return false;
    if (isLabelValue(line)) return false;
    // Lines ending with sentence punctuation are probably prose
    if (/[.!?]$/.test(line)) return false;
    return true;
  }

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    // Blank line → line break
    if (trimmed === '') {
      closeOpenList();
      result.push('<br>');
      continue;
    }

    // Markdown heading: # → h2, ## → h2, ### → h3
    const mdH = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (mdH) {
      closeOpenList();
      const tag = mdH[1].length <= 2 ? 'h2' : 'h3';
      const content = processInlineMarkdown(escapeHtml(mdH[2]));
      result.push(`<${tag}>${content}</${tag}>`);
      isFirstContent = false;
      continue;
    }

    // Bullet list item: - * •
    const bullet = trimmed.match(/^[-*•]\s+(.+)$/);
    if (bullet) {
      if (inNumberedList) { result.push('</ol>'); inNumberedList = false; }
      if (!inBulletList) { result.push('<ul>'); inBulletList = true; }
      const content = processInlineMarkdown(escapeHtml(bullet[1]));
      result.push(`<li>${content}</li>`);
      isFirstContent = false;
      continue;
    }

    // Numbered list item: 1. 2) etc.
    const numbered = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
    if (numbered) {
      if (inBulletList) { result.push('</ul>'); inBulletList = false; }
      if (!inNumberedList) { result.push('<ol>'); inNumberedList = true; }
      const content = processInlineMarkdown(escapeHtml(numbered[2]));
      result.push(`<li>${content}</li>`);
      isFirstContent = false;
      continue;
    }

    // Non-list content: close any open list
    closeOpenList();

    const nextNonBlank = getNextNonBlank(i + 1);

    // First content line as report title (h2)
    if (isFirstContent && looksLikeHeading(trimmed) && trimmed.length <= 80) {
      if (nextNonBlank && (isLabelValue(nextNonBlank) || looksLikeHeading(nextNonBlank))) {
        const content = processInlineMarkdown(escapeHtml(trimmed));
        result.push(`<h2>${content}</h2>`);
        isFirstContent = false;
        continue;
      }
    }

    // Section heading (h3): short line followed by label/value content
    if (looksLikeHeading(trimmed) && nextNonBlank && isLabelValue(nextNonBlank)) {
      const content = processInlineMarkdown(escapeHtml(trimmed));
      result.push(`<h3>${content}</h3>`);
      isFirstContent = false;
      continue;
    }

    // Label/value line: bold label, plain value
    if (isLabelValue(trimmed)) {
      const ci = trimmed.indexOf(':');
      const label = trimmed.substring(0, ci + 1);
      const val = trimmed.substring(ci + 1).trim();
      const labelHtml = processInlineMarkdown(escapeHtml(label));
      const valHtml = processInlineMarkdown(escapeHtml(val));
      result.push(`<p><strong>${labelHtml}</strong> ${valHtml}</p>`);
      isFirstContent = false;
      continue;
    }

    // Ordinary paragraph
    const content = processInlineMarkdown(escapeHtml(trimmed));
    result.push(`<p>${content}</p>`);
    isFirstContent = false;
  }

  closeOpenList();
  return result.join('');
}

function normaliseEditorValue(value: string): string {
  if (!value || !value.trim()) return '';

  // Detect if value already contains meaningful HTML from our allowed set
  const hasAllowedHtml = ALLOWED_TAGS.some(tag =>
    new RegExp(`<${tag}[\\s>]`, 'i').test(value)
  );

  if (hasAllowedHtml) {
    // Already valid HTML — just sanitise (idempotent for clean content)
    return DOMPurify.sanitize(value, { ALLOWED_TAGS, ALLOWED_ATTR });
  }

  // Contains HTML tags but not from our allowed set — normalise then sanitise
  if (/<[a-z][^>]*>/i.test(value)) {
    const normalised = normaliseRichHtml(value);
    return DOMPurify.sanitize(normalised, { ALLOWED_TAGS, ALLOWED_ATTR });
  }

  // Plain text — convert to structured HTML then sanitise
  const html = convertPlainTextToStructuredHtml(value);
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

// ─── Component ───

export function RichTextEditor({
  value,
  onChange,
  maxLength = config.limits.annualSummary,
  placeholder,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const linkUrlRef = useRef<HTMLInputElement>(null);
  const savedRange = useRef<Range | null>(null);
  const initialValueRef = useRef(normaliseEditorValue(value));
  const lastValidHtml = useRef(initialValueRef.current);

  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      const clean = normaliseEditorValue(value);
      if (clean !== lastValidHtml.current) {
        editorRef.current.innerHTML = clean;
        lastValidHtml.current = clean;
      }
    }
  }, [value]);

  const exec = useCallback((cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    handleInput();
  }, []);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const raw = editorRef.current.innerHTML;
    const clean = DOMPurify.sanitize(raw, { ALLOWED_TAGS, ALLOWED_ATTR });
    if (clean.length <= maxLength) {
      lastValidHtml.current = clean;
      onChange(clean);
    } else {
      editorRef.current.innerHTML = lastValidHtml.current;
      if (editorRef.current.childNodes.length > 0) {
        const newRange = document.createRange();
        newRange.selectNodeContents(editorRef.current);
        newRange.collapse(false);
        const newSel = window.getSelection();
        newSel?.removeAllRanges();
        newSel?.addRange(newRange);
      }
    }
  }, [onChange, maxLength]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    // If no usable content, do not interfere with other clipboard behaviour
    if (!html.trim() && !text.trim()) return;

    e.preventDefault();

    let insertHtml: string;

    if (html.trim()) {
      // Rich HTML from clipboard — normalise and sanitise
      const normalised = normaliseRichHtml(html);
      insertHtml = DOMPurify.sanitize(normalised, { ALLOWED_TAGS, ALLOWED_ATTR });
    } else {
      // Plain text — convert to structured HTML then sanitise
      const structured = convertPlainTextToStructuredHtml(text);
      insertHtml = DOMPurify.sanitize(structured, { ALLOWED_TAGS, ALLOWED_ATTR });
    }

    if (insertHtml) {
      document.execCommand('insertHTML', false, insertHtml);
      handleInput();
    }
  }, [handleInput]);

  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0);
  }, []);

  const insertLink = useCallback(() => {
    const url = linkUrlRef.current?.value?.trim();
    if (!url) return;
    const sel = window.getSelection();
    if (savedRange.current) {
      sel?.removeAllRanges();
      sel?.addRange(savedRange.current);
    }
    document.execCommand('createLink', false, url);
    handleInput();
    if (linkUrlRef.current) linkUrlRef.current.value = '';
  }, [handleInput]);

  const insertTable = useCallback(() => {
    const tbl = '<table style="border-collapse:collapse;width:100%"><thead><tr><th style="border:1px solid #ccc;padding:4px">Heading</th><th style="border:1px solid #ccc;padding:4px">Heading</th></tr></thead><tbody><tr><td style="border:1px solid #ccc;padding:4px">&nbsp;</td><td style="border:1px solid #ccc;padding:4px">&nbsp;</td></tr></tbody></table>';
    document.execCommand('insertHTML', false, tbl);
    handleInput();
  }, [handleInput]);

  const charCount = value.length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 rounded-lg border border-slate-200">
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('formatBlock', 'h2')} className="p-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Heading 2">H2</button>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('formatBlock', 'h3')} className="p-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Heading 3">H3</button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('bold')} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Bold"><SafeIcon icon={FiBold} className="w-4 h-4" /></button>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('italic')} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Italic"><SafeIcon icon={FiItalic} className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('insertUnorderedList')} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Bullet list"><SafeIcon icon={FiList} className="w-4 h-4" /></button>
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('insertOrderedList')} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Numbered list"><span className="text-xs font-medium">1.</span></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={insertTable} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Insert table"><SafeIcon icon={FiMinus} className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={saveSelection} className="p-1.5 text-slate-600 hover:bg-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" title="Save selection for link"><SafeIcon icon={FiLink} className="w-4 h-4" /></button>
        <input ref={linkUrlRef} type="url" placeholder="URL" className="flex-1 min-w-[120px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0F766E]" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertLink(); } }} />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={insertLink} className="p-1.5 text-xs text-[#0F766E] hover:bg-slate-200 rounded font-medium focus:outline-none focus:ring-1 focus:ring-[#0F766E]">Insert</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onBlur={() => {
          if (editorRef.current && editorRef.current.innerHTML !== lastValidHtml.current) {
            editorRef.current.innerHTML = lastValidHtml.current;
          }
        }}
        dangerouslySetInnerHTML={{ __html: initialValueRef.current }}
        data-placeholder={placeholder}
        className="min-h-[200px] max-h-[400px] overflow-y-auto p-3 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0F766E] [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-slate-400"
      />
      <p className="text-xs text-slate-400 text-right">
        {charCount.toLocaleString()} / {maxLength.toLocaleString()} characters
      </p>
    </div>
  );
}